import { Component, Output, EventEmitter, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ICP_HORIZONTAL_TAB_PARAM, ICP_VERTICAL_TAB_PARAM, VerticalMenuTabsEnum } from 'src/app/modals/icp-automations';
import { ModalNavigationEnum } from 'src/app/shared/constants';
import { IsLoading, LoadingWrapper } from 'src/app/shared/decorators';
import { IcpAgentDashboardListDto, } from 'src/app/shared/dto';
import { IcpMonthlySummaryDto, IcpCardSummaryDetail, IcpSurgedCompaniesDto } from 'src/app/shared/dto/icp-monthly-summary.dto';
import { PredictiveIntentAutomationEnum, FeatureFlagsEnum, ResultTabsetUrlsEnum } from 'src/app/shared/enums';
import { DashboardApiService } from 'src/app/shared/services/api';
import { cloneDeep } from 'lodash-es';

const LOADER_ICP_AGENTS: string = 'get-icp-agents';
const LOADER_MONTHLY_EXPORT_SUMMARY: string = 'get-monthly-export-summary';
const LOADER_MONTHLY_REPORT_SUMMARY: string = 'get-monthly-report-summary';
const LOADER_MONTHLY_COMBINED_SUMMARY: string = 'get-monthly-combined-summary';

@UntilDestroy()
@Component({
    selector: 'app-icp-card-container',
    templateUrl: './icp-card-container.component.html',
    styleUrls: ['../../common-reporting-dashboard.scss'],
    standalone: false
})
export class IcpCardContainerComponent implements OnInit {
    @Output() saveSearchTrigger = new EventEmitter();

    // POR-11002 Track-history single-card mode: hide the dropdown + preselect this agent.
    @Input() hideAgentSelector: boolean = false;
    @Input() preselectedAgentId: string | number | null = null;

    @IsLoading(LOADER_ICP_AGENTS) isGetIcpAgentsLoading$: Observable<boolean>;
    @IsLoading(LOADER_MONTHLY_EXPORT_SUMMARY) isGetMonthlyExportSummaryLoading$: Observable<boolean>;
    @IsLoading(LOADER_MONTHLY_REPORT_SUMMARY) isGetMonthlyReportSummaryLoading$: Observable<boolean>;
    @IsLoading(LOADER_MONTHLY_COMBINED_SUMMARY) isGetMonthlyCombinedSummaryLoading$: Observable<boolean>;

    isShowChart: boolean = true;
    isWeeklyAgentsNotRun: boolean = false;
    selectedOption: IcpAgentDashboardListDto | null = null;
    icpMonthlyExportSummary: IcpMonthlySummaryDto;
    icpMonthlyReportSummary: IcpMonthlySummaryDto;
    icpMonthlyCombinedSummary: IcpMonthlySummaryDto;
    icpCardSummaryDetail: IcpCardSummaryDetail;
    
    readonly featureFlagsEnum = FeatureFlagsEnum;
    readonly resultTabsetUrlsEnum = ResultTabsetUrlsEnum;

    readonly isIcpLoading$: Observable<boolean>;
    readonly getIcpAgents$: Observable<IcpAgentDashboardListDto[]>;
    private agentSurgedIdSubject = new BehaviorSubject<number | null>(null);
    private reportId: string = null;

    constructor(private readonly dashboardApiService: DashboardApiService, 
        private readonly router: Router) {

        this.isIcpLoading$ = combineLatest([
            this.isGetIcpAgentsLoading$,
            this.isGetMonthlyExportSummaryLoading$,
            this.isGetMonthlyReportSummaryLoading$,
            this.isGetMonthlyCombinedSummaryLoading$
        ]).pipe(
            map((loaders: boolean[]) => loaders.some((loader: boolean) => loader))
        );

        this.getIcpAgents$ = this.getIcpAgents().pipe(catchError(() => of([])), shareReplay(1));
        this.agentSurgedIdSubject.pipe(
            switchMap((id: number | null) => id ? this.dashboardApiService.getICPSurgedCompanies(id) : of(null)),
            untilDestroyed(this)
        ).subscribe((data: IcpSurgedCompaniesDto) => this.reportId = data?.report_id || null);
    }

    ngOnInit(): void {
        // Skipped in track-history mode; here not in the constructor, where @Input()s are unset.
        if (!(this.hideAgentSelector && this.preselectedAgentId != null)) {
            this.getIcpMonthlyCombinedSummaryData();
        }
        if (this.preselectedAgentId == null) return;
        // POR-11002 defect-2: always select the requested automation, even one not yet in the agents list (never run).
        this.getIcpAgents$.pipe(
            take(1),
            map((agents: IcpAgentDashboardListDto[]) =>
                agents?.find((a) => String(a?.id) === String(this.preselectedAgentId))
                ?? { id: Number(this.preselectedAgentId), name: '', email_only: false }),
            untilDestroyed(this),
        ).subscribe((agent: IcpAgentDashboardListDto) => this.updateSelectedOption(agent));
    }

    updateSelectedOption(value: IcpAgentDashboardListDto | null) {
        this.selectedOption = value;
        if (this.selectedOption) {
            if (this.selectedOption?.email_only) {
                this.agentSurgedIdSubject.next(value?.id || null);
                this.getIcpMonthlyReportSummaryData()
            } else {
                this.getIcpMonthlyExportSummaryData();
            }
        } else {
            this.getIcpMonthlyCombinedSummaryData();
        }
    }

    onOpenIcpSettings(): void {
        this.router.navigate(['/', { outlets: { modal: [ModalNavigationEnum.ICP_AUTOMATIONS] } }], {
            queryParams: {
                [ICP_VERTICAL_TAB_PARAM]: VerticalMenuTabsEnum.ICP_AUTOMATIONS,
                [ICP_HORIZONTAL_TAB_PARAM]: PredictiveIntentAutomationEnum.NEW_AUTOMATION,
            },
            queryParamsHandling: 'merge'
        })
    }

    showReport(): void {
        if (this.selectedOption?.email_only && this.reportId) {
            this.router.navigate(['results/search'], {
                queryParams: { icpReportId: this.reportId }
            });
        } else {
            this.saveSearchTrigger.emit({saved_search_id: this.icpCardSummaryDetail?.saved_search_id});
        }
    }

    private getIcpMonthlyCombinedSummaryData(): void {
        this.getIcpMonthlyCombinedSummary().pipe(untilDestroyed(this)).subscribe((res: IcpMonthlySummaryDto) => {
            this.icpMonthlyCombinedSummary = res;
            this.isWeeklyAgentsNotRun = res.weekly_summary.reduce((sum, entry) => sum + (entry.total_company_count || 0) + (entry.total_contact_count || 0) + (entry.all_companies_count || 0), 0) === 0;
        }, () => { this.icpMonthlyCombinedSummary = null })
    }

    private getIcpMonthlyExportSummaryData(): void {
        this.getIcpMonthlyExportSummary().pipe(untilDestroyed(this)).subscribe((res: IcpMonthlySummaryDto) => {
            this.icpMonthlyExportSummary = res;
            this.icpCardSummaryDetail = cloneDeep(res.intent_card_summary_detail);
            this.isWeeklyAgentsNotRun = res.weekly_summary.reduce((sum, entry) => sum + (entry.total_company_count || 0) + (entry.total_contact_count || 0), 0) === 0;
        }, () => { this.icpMonthlyExportSummary = null })
    }

    private getIcpMonthlyReportSummaryData(): void {
        this.getIcpMonthlyReportSummary().pipe(untilDestroyed(this)).subscribe((res: IcpMonthlySummaryDto) => {
            this.icpMonthlyReportSummary = res;
            this.icpCardSummaryDetail = cloneDeep(res.intent_card_summary_detail);
            this.isWeeklyAgentsNotRun = res.weekly_summary.reduce((sum, entry) => sum + (entry.total_company_count || 0) + (entry.total_contact_count || 0), 0) === 0;
        }, () => { this.icpMonthlyReportSummary = null })
    }

    @LoadingWrapper(LOADER_ICP_AGENTS)
    private getIcpAgents(): Observable<IcpAgentDashboardListDto[]> {
        return this.dashboardApiService.getIcpAgents()
    }

    @LoadingWrapper(LOADER_MONTHLY_EXPORT_SUMMARY)
    private getIcpMonthlyExportSummary(): Observable<IcpMonthlySummaryDto> {
        return this.selectedOption ? this.dashboardApiService.getIcpMonthlyExportSummary(this.selectedOption.id) : of(null);
    }

    @LoadingWrapper(LOADER_MONTHLY_REPORT_SUMMARY)
    private getIcpMonthlyReportSummary(): Observable<IcpMonthlySummaryDto> {
        return this.selectedOption ? this.dashboardApiService.getIcpMonthlyReportSummary(this.selectedOption?.id) : of(null);
    }

    @LoadingWrapper(LOADER_MONTHLY_COMBINED_SUMMARY)
    private getIcpMonthlyCombinedSummary(): Observable<IcpMonthlySummaryDto> {
        return this.dashboardApiService.getIcpMonthlyCombinedSummary();
    }
}
